/**
 * Hook for fetching home catalog (available tabs/shortcuts)
 * 
 * Uses React Query with semi-static caching strategy (30 minutes)
 * Invalidates on permissions/features change
 */

import { useQuery, useQueryClient, UseQueryOptions, keepPreviousData } from '@tanstack/react-query';
import { getHomeCatalog } from '../../api/homeApi';
import { homeKeys } from '../../lib/queryKeys';
import { queryOptions } from '../../lib/queryClient';

export interface HomeTab {
  id: string;
  label: string;
  description: string;
  iconKey: string;
  route: string;
  permissions: string[];
  roles: string[];
  defaultRank: number;
  feature?: string;
}

/**
 * Hook to fetch home catalog
 * 
 * @param company - Company slug (required)
 * @param options - Additional React Query options
 * @returns Query result with catalog data
 * 
 * @example
 * ```tsx
 * const { data: catalog, isLoading } = useHomeCatalog(company);
 * const tabs = catalog || [];
 * ```
 */
export function useHomeCatalog(
  company: string | undefined,
  options?: Omit<UseQueryOptions<HomeTab[]>, 'queryKey' | 'queryFn'>
) {
  const queryClient = useQueryClient();
  
  // Check for cached data to use as initialData (like Clients page)
  const cachedData = queryClient.getQueryData<HomeTab[]>(homeKeys.catalog(company || ''));
  
  return useQuery<HomeTab[]>({
    queryKey: homeKeys.catalog(company || ''),
    queryFn: async () => {
      if (!company) {
        throw new Error('Company is required');
      }
      try {
        console.log('[useHomeCatalog] queryFn called', { company });
        
        // 🔥 CRITICAL: Add timeout wrapper to ensure query never hangs forever
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error('getHomeCatalog timeout after 35 seconds'));
          }, 35000); // 35 seconds (slightly longer than API timeout of 30s)
        });
        
        const data = await Promise.race([
          getHomeCatalog(company),
          timeoutPromise,
        ]);
        
        console.log('[useHomeCatalog] queryFn resolved', { dataLength: Array.isArray(data) ? data.length : 'not array' });
        return data || [];
      } catch (err: any) {
        console.error('[useHomeCatalog] queryFn error', {
          error: err?.message,
          code: err?.code,
          status: err?.response?.status,
        });
        // 🔥 CRITICAL: Always throw to ensure React Query knows the query failed
        throw err;
      }
    },
    enabled: !!company,
    retry: 1, // Only retry once to avoid long waits
    initialData: cachedData, // Use cached data if available (from prefetch/bootstrap)
    placeholderData: cachedData || keepPreviousData, // Use cached data as placeholder for instant render
    // Cache-first: treat as static until explicitly invalidated (plan/badge changes)
    staleTime: Infinity, // Home catalog is static - only invalidate when plan/badge changes
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    ...options,
  });
}


